import dynamic from "next/dynamic";
import NextLink from "next/link";

const Link = dynamic(() => import("@/components/ui/Link"), { ssr: false });
import { getCategoryUrl } from "@/lib/blog";
import { sanitizeHTML } from "@/lib/utils";
import Image from 'next/image';
import { getReadableImageUrl } from '@/lib/directus';
import { htmlToPlainText, cleanBlogContent } from '../utils';
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";

export interface BlogPostSectionData {
  title: string;
  published_at: string;
  content: string;
  featured_image?: {
    id: string;
    filename_download: string;
  };
  author?: {
    slug: string;
    title: string;
  };
  categories: {
    blog_categories_id: {
      title: string;
      slug: string;
    };
  }[];
  previousPost?: {
    slug: string;
  };
  nextPost?: {
    slug: string;
  };
}

interface BlogPostSectionProps {
  data: BlogPostSectionData;
}

export default function BlogPostSection({ data }: BlogPostSectionProps) {
  // console.log('BlogPostSection data:', data);
  // console.log('Featured image:', data.featured_image);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = htmlToPlainText(data.title) || '';

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    }
  };

  return (
		<>
			<section className='BlogHeroPostSection w-full bg-primary py-12 md:py-16'>
				<div className='container text-white grid gap-2 px-8 lg:px-16'>
					<h1
						dangerouslySetInnerHTML={sanitizeHTML(data.title)}
						className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white mb-8"
					/>
				<div className='flex flex-col gap-1'>
					<div className='flex items-center gap-2 text-sm font-greycliff text-white/90'>
						<NextLink
							href='/about/meet-dr-bader/'
							className='font-semibold text-white hover:text-white/80 transition-colors'
						>
							Bradford Bader M.D.
						</NextLink>
						<span>·</span>
						<time suppressHydrationWarning>
							{new Date(data.published_at).toLocaleDateString('en-US', {
								month: 'long',
								day: 'numeric',
								year: 'numeric',
							})}
						</time>
					</div>
					{data.categories && data.categories.filter(c => c.blog_categories_id).length > 0 && (
						<div className='text-sm font-greycliff text-white/90'>
							<span className='font-medium'>Posted In: </span>
							{data.categories
								.filter(c => c.blog_categories_id)
								.map((cat, idx, arr) => (
									<span key={cat.blog_categories_id.slug}>
										<NextLink
											href={getCategoryUrl(cat.blog_categories_id)}
											className='text-white hover:text-white/80 transition-colors underline underline-offset-2'
										>
											{cat.blog_categories_id.title}
										</NextLink>
										{idx < arr.length - 1 && <span className='mx-1'>•</span>}
									</span>
								))}
						</div>
					)}
				</div>
				</div>
			</section>
			<Breadcrumbs />
			<section className='BlogPostSection pb-16 py-8 md:py-24'>
				<div className='container max-w-[1024px] mx-auto md:px-0 px-8 md:px-16'>
					{/* Navigation Buttons */}
					<div className='flex flex-col md:flex-row md:justify-between md:items-center gap-4'>
						<Link
							href={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}`}
							className='flex text-primary h-[4.375rem] px-[3.125rem] rounded-[3.125rem] border border-primary items-center hover:bg-primary hover:text-white transition-colors justify-center'
						>
							<span className="text-inherit text-xl font-greycliff leading-7">Back to Main Blog</span>
						</Link>
						<div className='flex gap-4 w-full md:w-auto'>
							{data.previousPost && (
								<Link
									href={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${data.previousPost.slug}`}
									className='flex text-white bg-primary h-[4.375rem] px-[3.125rem] rounded-[3.125rem] border border-primary items-center hover:bg-white hover:text-primary transition-colors justify-center flex-1 md:flex-none'
								>
									<span className="text-inherit text-xl font-greycliff leading-7">Previous</span>
								</Link>
							)}
							{data.nextPost && (
								<Link
									href={`/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${data.nextPost.slug}`}
									className='flex text-white bg-primary h-[4.375rem] px-[3.125rem] rounded-[3.125rem] border border-primary items-center hover:bg-white hover:text-primary transition-colors justify-center flex-1 md:flex-none'
								>
									<span className="text-inherit text-xl font-greycliff leading-7">Next</span>
								</Link>
							)}
						</div>
					</div>
					{data.featured_image && (
						<div className='relative aspect-[8/9] md:aspect-[16/9] w-full md:mx-0 my-8'>
							<Image
								src={
									data.featured_image
										? getReadableImageUrl(
												data.featured_image.id,
												data.featured_image.filename_download
										  )
										: '/images/blog-placeh2.webp'
								}
								alt={htmlToPlainText(data.title) || ''}
								fill
								className='object-cover'
								sizes='(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw'
								quality={95}
								loading='lazy'
							/>
						</div>
					)}
					<div
						className="grid gap-8 text_box 
							text-[1rem] font-normal leading-normal md:text-xl text-primary font-greycliff md:leading-relaxed
							[&>p]:mb-4 [&>p:last-child]:mb-0 
							[&>p_a]:underline hover:[&>p_a]:no-underline
							[&>h3_a]:underline hover:[&>h3_a]:no-underline
							[&>h2_a]:underline hover:[&>h2_a]:no-underline
							[&>ul_a]:underline hover:[&>ul_a]:no-underline
							[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
							[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
							[&_li]:mb-1
							[&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:[&>*]:text-xl
							[&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:mb-6"
						dangerouslySetInnerHTML={sanitizeHTML(
							cleanBlogContent(data.content)
						)}
					></div>

					{/* Social Share Buttons */}
					<div className='mt-12 flex items-center justify-end gap-6'>
						<span className='text-lg text-primary'>Share:</span>
						<div className='flex items-center gap-4'>
							<a
								href={shareLinks.facebook}
								target='_blank'
								rel='noopener noreferrer'
								className='w-10 h-10 flex items-center justify-center rounded-full border border-primary text-primary hover:bg-gray-50 transition-colors'
								aria-label='Share on Facebook'
							>
								<svg
									className='w-5 h-5'
									viewBox='0 0 24 24'
									fill='currentColor'
								>
									<path d='M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z' />
								</svg>
							</a>
							<a
								href={shareLinks.twitter}
								target='_blank'
								rel='noopener noreferrer'
								className='w-10 h-10 flex items-center justify-center rounded-full border border-primary text-primary hover:bg-gray-50 transition-colors'
								aria-label='Share on Twitter'
							>
								<svg
									className='w-5 h-5'
									viewBox='0 0 24 24'
									fill='currentColor'
								>
									<path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
								</svg>
							</a>
							<a
								href={shareLinks.linkedin}
								target='_blank'
								rel='noopener noreferrer'
								className='w-10 h-10 flex items-center justify-center rounded-full border border-primary text-primary hover:bg-gray-50 transition-colors'
								aria-label='Share on LinkedIn'
							>
								<svg
									className='w-5 h-5'
									viewBox='0 0 24 24'
									fill='currentColor'
								>
									<path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' />
								</svg>
							</a>
						</div>
					</div>
				</div>
			</section>
		</>
	)
}
